/**
 * In-memory stand-in for api-service's uploaded PowerPoint template routes (pd7 CONTRACT §2.4).
 * Like the backend: an upload over 25 MB is 413 `master_too_large`, anything but a non-empty
 * .pptx/.potx is 422 `master_not_pptx` (the mock trusts the name, api-service reads the bytes); a
 * stored upload reads `preparing` for its first reads, then `ready` with a neutral synthetic look
 * (PowerPoint's default layout names and Office colours) whose preview kit follows a few reads later
 * (as action-engine's does, one keeper pass after ready), or `failed` with the reader's message when
 * `setMockMasterReadFailure` was set before the upload.
 * Re-exported from distributionsApi.mock.ts, so the API clients reach it the same way.
 */
import type { UploadProgressCallback } from "../assets/assets.types";
import { MockDomainError, mockNotFoundError } from "../mockDomainError";
import { MASTER_ERROR_CODES, MASTER_LIMITS } from "./distributionMasters.types";
import type { DistributionMasterLookApi, DistributionMasterUploadApi } from "./distributions.types";

const LATENCY_MS = process.env.NODE_ENV === "test" ? 0 : 250;
const wait = (ms = LATENCY_MS): Promise<void> => (ms ? new Promise((resolve) => setTimeout(resolve, ms)) : Promise.resolve());
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

/** Reads that still answer `preparing`, so the UI shows "Reading your template…" for a few polls. */
export const MOCK_MASTER_PREPARING_READS = 2;
/** Reads that answer `ready` with no kit yet, so the UI shows "Preparing a preview…" for a few polls. */
export const MOCK_MASTER_KIT_READS = 2;
/** The kit the mock answers once drawn: the page size, no slide images (the preview falls back to tokens). */
export const MOCK_MASTER_KIT: Readonly<NonNullable<DistributionMasterLookApi["kit"]>> = {
  version: 1,
  stale: false,
  slide: { width_pt: 960, height_pt: 540 },
  slides: {},
};

/** What a default PowerPoint file's slide master gives the reader (§1.4): neutral, no tenant values. */
export const MOCK_MASTER_LAYOUTS: Readonly<Record<string, string>> = {
  cover_open: "Title Slide",
  story_summary: "Title and Content",
  kpi_row3: "Title Only",
  chart_full: "Title and Content",
  chart_split: "Two Content",
  text_body: "Title and Content",
  cover_close: "Title Slide",
};

/** The Office colour scheme mapped the reader's way (textHigh = dk1, chartRamp = accent1–6, …). */
export const MOCK_MASTER_PALETTE: Readonly<Record<string, string | string[]>> = {
  textHigh: "#000000",
  textMedium: "#44546A",
  canvasLight: "#FFFFFF",
  purplePrimary: "#4472C4",
  chartRamp: ["#4472C4", "#ED7D31", "#A5A5A5", "#FFC000", "#5B9BD5", "#70AD47"],
  ruleGray: "#E7E6E6",
  accentPositive: "#16A34A",
  accentNegative: "#DC2626",
};

export const MOCK_MASTER_FONT = "Calibri";

interface MockMaster {
  id: string;
  fileName: string;
  reads: number;
  /** The reader's message when it fails; null reads `ready`. */
  failure: string | null;
}

let seq = 0;
const nextId = (): string => `master-${(++seq).toString(36)}-${Date.now().toString(36)}`;
let masters = new Map<string, MockMaster>();
let nextFailure: string | null = null;

const isPowerPointName = (name: string): boolean =>
  (MASTER_LIMITS.extensions as readonly string[]).includes(/\.([^./\\]+)$/.exec(name.trim())?.[1]?.toLowerCase() ?? "");

const lookOf = (master: MockMaster): DistributionMasterLookApi => {
  const base = { master_id: master.id, file_name: master.fileName };
  if (master.reads <= MOCK_MASTER_PREPARING_READS) return { ...base, state: "preparing", message: null, layouts: null };
  if (master.failure) return { ...base, state: "failed", message: master.failure, layouts: null };
  return {
    ...base,
    state: "ready",
    message: null,
    layouts: { ...MOCK_MASTER_LAYOUTS },
    tone: "light",
    palette: clone(MOCK_MASTER_PALETTE) as Record<string, string | string[]>,
    font_family: MOCK_MASTER_FONT,
    fonts: [],
    kit: master.reads > MOCK_MASTER_PREPARING_READS + MOCK_MASTER_KIT_READS ? clone(MOCK_MASTER_KIT) : null,
  };
};

/** Mirrors api-service: the size first (413), then the content (422); then a few progress steps. */
export const mockUploadDistributionMaster = async (file: File, onProgress?: UploadProgressCallback): Promise<DistributionMasterUploadApi> => {
  await wait();
  if (file.size > MASTER_LIMITS.maxBytes) {
    throw new MockDomainError({
      status: 413,
      code: MASTER_ERROR_CODES.tooLarge,
      message: `“${file.name}” is larger than 25 MB. Save a smaller copy, then upload it again.`,
    });
  }
  if (!isPowerPointName(file.name) || file.size === 0) {
    throw new MockDomainError({
      status: 422,
      code: MASTER_ERROR_CODES.notPptx,
      message: `“${file.name}” isn’t a PowerPoint file we can open. Upload a .pptx or .potx file.`,
    });
  }
  for (const percent of [35, 70]) {
    onProgress?.({ phase: "uploading", percent });
    await wait();
  }
  onProgress?.({ phase: "processing", percent: 100 });
  const id = nextId();
  masters.set(id, { id, fileName: file.name, reads: 0, failure: nextFailure });
  return { master_id: id, file_name: file.name, size_bytes: file.size };
};

/** Each read advances the reader: `preparing` for the first reads, then its outcome. */
export const mockGetDistributionMaster = async (masterId: string): Promise<DistributionMasterLookApi> => {
  await wait();
  const master = masters.get(masterId);
  if (!master) throw mockNotFoundError("We couldn’t find this PowerPoint file. Upload it again.");
  master.reads += 1;
  return clone(lookOf(master));
};

/** The look as a template read carries it; doesn't advance the reader. Null for an unknown id. */
export const mockMasterLookFor = (masterId: string | null | undefined): DistributionMasterLookApi | null => {
  const master = masterId ? masters.get(masterId) : undefined;
  return master ? clone(lookOf(master)) : null;
};

/** Test and story helper: uploads after this read `failed` with `message` (null: `ready` again). */
export const setMockMasterReadFailure = (message: string | null): void => {
  nextFailure = message;
};

/** Test and story helper: forget every upload. */
export const resetMastersMock = (): void => {
  masters = new Map();
  nextFailure = null;
  seq = 0;
};
